import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  useStudyPlans,
  saveStudyPlans,
  seedStudyPlansIfMissing,
  savePricingExtras,
} from '../../hooks/useStudyPlans';
import { computeSalePrice, formatUsd, normalizePlan } from '../../data/studyPlans';

function emptyPlan() {
  return normalizePlan({
    id: `plan-${Date.now()}`,
    name: 'New Study Plan',
    durationLabel: '30 days of full access',
    days: 30,
    regularPrice: 200,
    discountPercent: 50,
    badge: '',
    featured: false,
    description: '',
    features: ['Full course access'],
  });
}

export default function PricingPlansPanel() {
  const { currentUser } = useAuth();
  const { plans: livePlans, extensions: liveExtensions, demoVideoUrl, examFee: liveExamFee, degreeFee: liveDegreeFee, adminContactPhone: livePhone, adminContactEmail: liveEmail, loading, fromServer } =
    useStudyPlans();
  const [drafts, setDrafts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [messageTone, setMessageTone] = useState('success');

  const [extDrafts, setExtDrafts] = useState([]);
  const [videoDraft, setVideoDraft] = useState('');
  const [examDraft, setExamDraft] = useState({ perSubjectUsd: 90, serviceAndTaxUsd: 30 });
  const [degreeDraft, setDegreeDraft] = useState({ diplomaTranscriptUsd: 140, serviceAndTaxUsd: 40 });
  const [phoneDraft, setPhoneDraft] = useState('');
  const [emailDraft, setEmailDraft] = useState('');
  const [savingExtras, setSavingExtras] = useState(false);
  const [extrasMessage, setExtrasMessage] = useState('');
  const [extrasTone, setExtrasTone] = useState('success');
  const [savingFees, setSavingFees] = useState(false);
  const [feesMessage, setFeesMessage] = useState('');
  const [feesTone, setFeesTone] = useState('success');

  useEffect(() => {
    setDrafts(livePlans.map((p) => ({ ...p, featuresText: (p.features || []).join('\n') })));
  }, [livePlans]);

  useEffect(() => {
    setExtDrafts(liveExtensions.map((o) => ({ ...o })));
  }, [liveExtensions]);

  useEffect(() => {
    setVideoDraft(demoVideoUrl || '');
  }, [demoVideoUrl]);

  useEffect(() => {
    setExamDraft({ ...liveExamFee });
  }, [liveExamFee]);

  useEffect(() => {
    setDegreeDraft({ ...liveDegreeFee });
  }, [liveDegreeFee]);

  useEffect(() => {
    setPhoneDraft(livePhone || '');
  }, [livePhone]);

  useEffect(() => {
    setEmailDraft(liveEmail || '');
  }, [liveEmail]);

  useEffect(() => {
    seedStudyPlansIfMissing(currentUser?.uid || '').catch((err) => {
      console.warn('Seed pricing skipped:', err);
    });
  }, [currentUser?.uid]);

  function updateDraft(index, patch) {
    setDrafts((prev) => {
      const next = [...prev];
      const current = { ...next[index], ...patch };
      if ('regularPrice' in patch || 'discountPercent' in patch) {
        current.price = computeSalePrice(current.regularPrice, current.discountPercent);
      }
      next[index] = current;
      return next;
    });
    setMessage('');
  }

  function addPlan() {
    const plan = emptyPlan();
    setDrafts((prev) => [...prev, { ...plan, featuresText: plan.features.join('\n') }]);
  }

  function removePlan(index) {
    if (drafts.length <= 1) {
      setMessageTone('error');
      setMessage('Keep at least one study plan.');
      return;
    }
    setDrafts((prev) => prev.filter((_, i) => i !== index));
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    try {
      const toSave = drafts.map((d) =>
        normalizePlan({
          ...d,
          features: String(d.featuresText || '')
            .split('\n')
            .map((l) => l.trim())
            .filter(Boolean),
        })
      );
      await saveStudyPlans(toSave, currentUser?.uid || '');
      setMessageTone('success');
      setMessage('Pricing, discounts, and plan content saved. The public website updates immediately.');
    } catch (err) {
      console.error(err);
      setMessageTone('error');
      setMessage(err?.message || 'Failed to save pricing settings.');
    } finally {
      setSaving(false);
    }
  }

  if (loading && drafts.length === 0) {
    return <div style={s.wrap}>Loading pricing settings…</div>;
  }

  return (
    <div style={s.wrap}>
      <div style={s.header}>
        <div>
          <h2 style={s.title}>Course pricing & plan content</h2>
          <p style={s.sub}>
            Set regular price, discount %, sale price (auto-calculated), plan copy, and features.
            These appear on Home, Pricing, and Contact. Course modules/lectures are edited in the subject tabs.
            {!fromServer && ' Using defaults until you save once.'}
          </p>
        </div>
        <button type="button" onClick={addPlan} style={s.secondaryBtn}>
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>add</span>
          Add plan
        </button>
      </div>

      {message && <div style={s.banner(messageTone)}>{message}</div>}

      <form onSubmit={handleSave}>
        {drafts.map((plan, index) => (
          <div key={plan.id || index} style={s.card}>
            <div style={s.cardTop}>
              <h3 style={s.cardTitle}>{plan.name || `Plan ${index + 1}`}</h3>
              <button type="button" onClick={() => removePlan(index)} style={s.deleteBtn}>
                Remove
              </button>
            </div>

            <div style={s.grid}>
              <label style={s.label}>
                Plan ID (used for subscriptions)
                <input
                  style={s.input}
                  value={plan.id}
                  onChange={(e) => updateDraft(index, { id: e.target.value.trim() })}
                  required
                />
              </label>
              <label style={s.label}>
                Display name
                <input
                  style={s.input}
                  value={plan.name}
                  onChange={(e) => updateDraft(index, { name: e.target.value })}
                  required
                />
              </label>
              <label style={s.label}>
                Duration label
                <input
                  style={s.input}
                  value={plan.durationLabel}
                  onChange={(e) => updateDraft(index, { durationLabel: e.target.value })}
                />
              </label>
              <label style={s.label}>
                Access days
                <input
                  type="number"
                  min={1}
                  style={s.input}
                  value={plan.days}
                  onChange={(e) => updateDraft(index, { days: Number(e.target.value) })}
                  required
                />
              </label>
              <label style={s.label}>
                Regular price (USD)
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  style={s.input}
                  value={plan.regularPrice}
                  onChange={(e) => updateDraft(index, { regularPrice: Number(e.target.value) })}
                  required
                />
              </label>
              <label style={s.label}>
                Discount %
                <input
                  type="number"
                  min={0}
                  max={100}
                  style={s.input}
                  value={plan.discountPercent}
                  onChange={(e) => updateDraft(index, { discountPercent: Number(e.target.value) })}
                  required
                />
              </label>
              <label style={s.label}>
                Sale price (auto)
                <input
                  style={{ ...s.input, background: '#f8fafc', fontWeight: 700 }}
                  value={formatUsd(computeSalePrice(plan.regularPrice, plan.discountPercent))}
                  readOnly
                />
              </label>
              <label style={s.label}>
                Badge text (optional)
                <input
                  style={s.input}
                  value={plan.badge || ''}
                  onChange={(e) => updateDraft(index, { badge: e.target.value })}
                  placeholder="Most popular"
                />
              </label>
            </div>

            <label style={{ ...s.label, marginTop: 12 }}>
              Short description
              <textarea
                style={s.textarea}
                rows={2}
                value={plan.description}
                onChange={(e) => updateDraft(index, { description: e.target.value })}
              />
            </label>

            <label style={{ ...s.label, marginTop: 12 }}>
              Features (one per line)
              <textarea
                style={s.textarea}
                rows={5}
                value={plan.featuresText}
                onChange={(e) => updateDraft(index, { featuresText: e.target.value })}
              />
            </label>

            <label style={s.check}>
              <input
                type="checkbox"
                checked={Boolean(plan.featured)}
                onChange={(e) => updateDraft(index, { featured: e.target.checked })}
              />
              Featured plan (dark card on website)
            </label>
          </div>
        ))}

        <button type="submit" disabled={saving} style={s.saveBtn(saving)}>
          {saving ? 'Saving…' : 'Save pricing & content'}
        </button>
      </form>

      {/* ---------- Extension add-on pricing + demo video ---------- */}
      <div style={{ ...s.card, marginTop: 28 }}>
        <div style={s.cardTop}>
          <h3 style={s.cardTitle}>"Need more days?" extension pricing</h3>
          <button
            type="button"
            onClick={() => setExtDrafts((prev) => [...prev, { days: 5, price: 20 }])}
            style={s.secondaryBtn}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>add</span>
            Add option
          </button>
        </div>
        <p style={s.sub}>
          Students with an active subscription can buy extra days from their dashboard. Edit the
          day/price options here.
        </p>

        <div style={{ display: 'grid', gap: 10, margin: '14px 0' }}>
          {extDrafts.map((opt, i) => (
            <div
              key={i}
              style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 10, alignItems: 'end' }}
            >
              <label style={s.label}>
                Extra days
                <input
                  type="number"
                  min={1}
                  style={s.input}
                  value={opt.days}
                  onChange={(e) =>
                    setExtDrafts((prev) =>
                      prev.map((o, idx) => (idx === i ? { ...o, days: Number(e.target.value) } : o))
                    )
                  }
                />
              </label>
              <label style={s.label}>
                Price (USD)
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  style={s.input}
                  value={opt.price}
                  onChange={(e) =>
                    setExtDrafts((prev) =>
                      prev.map((o, idx) => (idx === i ? { ...o, price: Number(e.target.value) } : o))
                    )
                  }
                />
              </label>
              <button
                type="button"
                onClick={() => setExtDrafts((prev) => prev.filter((_, idx) => idx !== i))}
                style={s.deleteBtn}
              >
                Remove
              </button>
            </div>
          ))}
        </div>

        <label style={{ ...s.label, marginTop: 6 }}>
          Demo video URL ("How our system works" — YouTube link or embed URL)
          <input
            style={s.input}
            value={videoDraft}
            onChange={(e) => setVideoDraft(e.target.value)}
            placeholder="https://www.youtube.com/watch?v=..."
          />
        </label>

        {extrasMessage && (
          <div style={{ ...s.banner(extrasTone), marginTop: 14, marginBottom: 0 }}>{extrasMessage}</div>
        )}

        <button
          type="button"
          disabled={savingExtras}
          style={{ ...s.saveBtn(savingExtras), marginTop: 14 }}
          onClick={async () => {
            setSavingExtras(true);
            setExtrasMessage('');
            try {
              await savePricingExtras(
                { extensions: extDrafts, demoVideoUrl: videoDraft },
                currentUser?.uid || ''
              );
              setExtrasTone('success');
              setExtrasMessage('Extension pricing and demo video saved.');
            } catch (err) {
              console.error(err);
              setExtrasTone('error');
              setExtrasMessage(err?.message || 'Failed to save extras.');
            } finally {
              setSavingExtras(false);
            }
          }}
        >
          {savingExtras ? 'Saving…' : 'Save extensions & video'}
        </button>
      </div>

      {/* ---------- GED Exam fee + Degree charges ---------- */}
      <div style={{ ...s.card, marginTop: 28 }}>
        <h3 style={s.cardTitle}>GED Exam fee structure</h3>
        <p style={s.sub}>
          Both amounts apply <strong>for each</strong> of the 4 GED exams (Mathematical Reasoning,
          Science, Social Studies, Reasoning Through Language Arts). Example with defaults: 1 subject
          = $120, all 4 = $480.
        </p>
        <div style={{ ...s.grid, marginTop: 14 }}>
          <label style={s.label}>
            Per-subject exam fee (USD)
            <input
              type="number"
              min={0}
              step="0.01"
              style={s.input}
              value={examDraft.perSubjectUsd}
              onChange={(e) =>
                setExamDraft((p) => ({ ...p, perSubjectUsd: Number(e.target.value) }))
              }
            />
          </label>
          <label style={s.label}>
            Per-subject service charges &amp; taxes (USD)
            <input
              type="number"
              min={0}
              step="0.01"
              style={s.input}
              value={examDraft.serviceAndTaxUsd}
              onChange={(e) =>
                setExamDraft((p) => ({ ...p, serviceAndTaxUsd: Number(e.target.value) }))
              }
            />
          </label>
        </div>
        <p style={{ ...s.sub, marginTop: 10 }}>
          Preview per exam: {formatUsd((Number(examDraft.perSubjectUsd) || 0) + (Number(examDraft.serviceAndTaxUsd) || 0))}
          {' · '}
          All 4: {formatUsd(((Number(examDraft.perSubjectUsd) || 0) + (Number(examDraft.serviceAndTaxUsd) || 0)) * 4)}
        </p>
      </div>

      <div style={{ ...s.card, marginTop: 16 }}>
        <h3 style={s.cardTitle}>GED Degree charges</h3>
        <p style={s.sub}>Diploma and transcript fee plus service charges &amp; taxes — both editable.</p>
        <div style={{ ...s.grid, marginTop: 14 }}>
          <label style={s.label}>
            Diploma &amp; transcript (USD)
            <input
              type="number"
              min={0}
              step="0.01"
              style={s.input}
              value={degreeDraft.diplomaTranscriptUsd}
              onChange={(e) =>
                setDegreeDraft((p) => ({ ...p, diplomaTranscriptUsd: Number(e.target.value) }))
              }
            />
          </label>
          <label style={s.label}>
            Service charges &amp; taxes (USD)
            <input
              type="number"
              min={0}
              step="0.01"
              style={s.input}
              value={degreeDraft.serviceAndTaxUsd}
              onChange={(e) =>
                setDegreeDraft((p) => ({ ...p, serviceAndTaxUsd: Number(e.target.value) }))
              }
            />
          </label>
        </div>
        <p style={{ ...s.sub, marginTop: 10 }}>
          Total:{' '}
          {formatUsd(
            (Number(degreeDraft.diplomaTranscriptUsd) || 0) + (Number(degreeDraft.serviceAndTaxUsd) || 0)
          )}
        </p>
      </div>

      <div style={{ ...s.card, marginTop: 16 }}>
        <h3 style={s.cardTitle}>Website contact info</h3>
        <p style={s.sub}>
          Phone and email shown on the public website top bar. Updates appear immediately on the frontend.
        </p>
        <label style={{ ...s.label, marginTop: 12 }}>
          Public phone number
          <input
            style={s.input}
            value={phoneDraft}
            onChange={(e) => setPhoneDraft(e.target.value)}
            placeholder="+1 800 123 456 789"
          />
        </label>
        <label style={{ ...s.label, marginTop: 12 }}>
          Public email address
          <input
            type="email"
            style={s.input}
            value={emailDraft}
            onChange={(e) => setEmailDraft(e.target.value)}
            placeholder="successbridgeacademy123@gmail.com"
          />
        </label>

        {feesMessage && (
          <div style={{ ...s.banner(feesTone), marginTop: 14, marginBottom: 0 }}>{feesMessage}</div>
        )}

        <button
          type="button"
          disabled={savingFees}
          style={{ ...s.saveBtn(savingFees), marginTop: 14 }}
          onClick={async () => {
            setSavingFees(true);
            setFeesMessage('');
            try {
              await savePricingExtras(
                {
                  examFee: examDraft,
                  degreeFee: degreeDraft,
                  adminContactPhone: phoneDraft,
                  adminContactEmail: emailDraft,
                },
                currentUser?.uid || ''
              );
              setFeesTone('success');
              setFeesMessage('Exam fee, degree charges, and website contact info saved.');
            } catch (err) {
              console.error(err);
              setFeesTone('error');
              setFeesMessage(err?.message || 'Failed to save fee structure.');
            } finally {
              setSavingFees(false);
            }
          }}
        >
          {savingFees ? 'Saving…' : 'Save exam, degree & contact'}
        </button>
      </div>
    </div>
  );
}

const s = {
  wrap: { flex: 1, minHeight: 0, padding: '24px 28px', overflowY: 'auto' },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 16,
    alignItems: 'flex-start',
    marginBottom: 20,
    flexWrap: 'wrap',
  },
  title: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 18,
    fontWeight: 800,
    color: '#1b365d',
    marginBottom: 6,
  },
  sub: { fontSize: 13, color: '#64748b', lineHeight: 1.6, maxWidth: 720 },
  secondaryBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '10px 14px',
    borderRadius: 10,
    border: '1.5px solid #e2e8f0',
    background: '#fff',
    color: '#1b365d',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
  },
  banner: (tone) => ({
    marginBottom: 16,
    padding: '12px 16px',
    borderRadius: 12,
    fontSize: 13,
    fontWeight: 600,
    ...(tone === 'error'
      ? { background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c' }
      : { background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#047857' }),
  }),
  card: {
    marginBottom: 16,
    padding: 20,
    borderRadius: 16,
    border: '1px solid #e2e8f0',
    background: '#fff',
  },
  cardTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 14,
  },
  cardTitle: { margin: 0, fontSize: 16, fontWeight: 800, color: '#1b365d' },
  deleteBtn: {
    border: '1px solid #fecaca',
    background: '#fff',
    color: '#dc2626',
    borderRadius: 8,
    padding: '6px 10px',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: 12,
  },
  label: { display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontWeight: 700, color: '#475569' },
  input: {
    padding: '10px 12px',
    borderRadius: 10,
    border: '1.5px solid #e2e8f0',
    fontSize: 14,
    fontWeight: 500,
    color: '#0f172a',
  },
  textarea: {
    padding: '10px 12px',
    borderRadius: 10,
    border: '1.5px solid #e2e8f0',
    fontSize: 14,
    fontWeight: 500,
    color: '#0f172a',
    fontFamily: 'inherit',
    resize: 'vertical',
  },
  check: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 14,
    fontSize: 13,
    fontWeight: 600,
    color: '#334155',
  },
  saveBtn: (disabled) => ({
    marginTop: 8,
    marginBottom: 32,
    padding: '12px 22px',
    borderRadius: 12,
    border: 'none',
    background: disabled ? '#94a3b8' : '#1b365d',
    color: '#fff',
    fontSize: 14,
    fontWeight: 700,
    cursor: disabled ? 'not-allowed' : 'pointer',
  }),
};
